/**
 * Products from a spreadsheet: get the template, choose the file, read the
 * check, import.
 *
 * The check is the point of this screen. Nothing is written until the owner has
 * seen how many products, pieces and how much stock the file will make, what new
 * categories and colours it will add, and every row the system could not read -
 * by its row number in their own file, so they can go and fix it.
 */
import { useRef, useState, type ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n, useT } from '../../core/i18n/index.js';
import type { StringKey } from '../../core/i18n/en.js';
import { useSettings } from '../../core/state/settings.js';
import { useToast } from '../../core/state/toast.js';
import { useSync } from '../../core/sync.js';
import { ApiError, api, download } from '../../core/api.js';
import { Button, Pill } from '../../core/ui/ui.js';

interface Check {
  sheet: string;
  headerRow: number;
  columns: Record<string, string>;
  ignored: string[];
  rows: number;
  totals: { newProducts: number; addedTo: number; newPieces: number; skipped: number; qty: number };
  create: { categories: string[]; brands: string[]; suppliers: string[]; colours: string[] };
  products: {
    row: number;
    code: string;
    nameEn: string;
    nameAr: string;
    existing: boolean;
    category: string;
    price: number;
    pieces: number;
    skipped: number;
    qty: number;
    sizes: string[];
    colours: string[];
  }[];
  moreProducts: number;
  errors: { row: number; code: string; value?: string }[];
  moreErrors: number;
  warnings: { code: string; n?: number }[];
}

interface Done {
  products: number;
  addedTo: number;
  pieces: number;
  qty: number;
}

const MAX_BYTES = 5 * 1024 * 1024;

function asBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).replace(/^data:[^,]*,/, ''));
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });
}

export function ProductImport() {
  const t = useT();
  const { lang } = useI18n();
  const { money } = useSettings();
  const toast = useToast();
  const navigate = useNavigate();
  const { refreshCatalog } = useSync();
  const picker = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<{ name: string; data: string } | null>(null);
  const [check, setCheck] = useState<Check | null>(null);
  const [problem, setProblem] = useState('');
  const [busy, setBusy] = useState<'' | 'checking' | 'importing' | 'template'>('');
  const [done, setDone] = useState<Done | null>(null);

  const say = (key: string, vars?: Record<string, string | number>) => t(key as StringKey, vars);

  const failed = (e: unknown) => {
    const x = e as ApiError;
    if (x.status === 0) return t('net.needOnline');
    const known = `import.file.${x.code}`;
    return say(known) !== known ? say(known) : x.message;
  };

  const runCheck = async (f: { name: string; data: string }) => {
    setBusy('checking');
    setProblem('');
    setCheck(null);
    try {
      setCheck(await api<Check>('/catalog/import/preview', { body: { fileName: f.name, data: f.data }, timeoutMs: 120_000 }));
    } catch (e) {
      setProblem(failed(e));
    } finally {
      setBusy('');
    }
  };

  const choose = async (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setDone(null);
    if (f.size > MAX_BYTES) {
      setFile(null);
      setCheck(null);
      setProblem(t('import.file.too_big'));
      return;
    }
    const picked = { name: f.name, data: await asBase64(f) };
    setFile(picked);
    await runCheck(picked);
  };

  const doImport = async () => {
    if (!file) return;
    setBusy('importing');
    try {
      const out = await api<Done>('/catalog/import', { body: { fileName: file.name, data: file.data }, timeoutMs: 180_000 });
      setDone(out);
      setCheck(null);
      setFile(null);
      // So the tills can sell what just arrived without waiting for their next refresh.
      void refreshCatalog().catch(() => undefined);
    } catch (e) {
      const x = e as ApiError;
      // Something changed since the check: check again and show why.
      if (x.code === 'import_has_errors') await runCheck(file);
      else toast.err(failed(e));
    } finally {
      setBusy('');
    }
  };

  const getTemplate = async () => {
    setBusy('template');
    try {
      await download(`/catalog/import/template?lang=${lang === 'ar' ? 'ar' : 'en'}`, 'products-template.xlsx');
    } catch (e) {
      toast.err(failed(e));
    } finally {
      setBusy('');
    }
  };

  const name = (p: { nameEn: string; nameAr: string }) => (lang === 'ar' ? p.nameAr || p.nameEn : p.nameEn || p.nameAr);
  const nothingNew = check ? check.totals.newPieces === 0 : true;

  return (
    <div className="col import">
      <div className="card">
        <div className="row wrap" style={{ alignItems: 'flex-start' }}>
          <p className="muted" style={{ flex: '1 1 320px', margin: 0 }}>{t('import.intro')}</p>
          <Button variant="ghost" onClick={() => navigate('/products')}>{t('common.back')}</Button>
        </div>
        <ol className="import-steps">
          <li>
            <span>{t('import.step1')}</span>
            <Button busy={busy === 'template'} onClick={() => void getTemplate()}>{t('import.getTemplate')}</Button>
          </li>
          <li>
            <span>{t('import.step2')}</span>
            <input
              ref={picker}
              type="file"
              accept=".xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
              style={{ display: 'none' }}
              onChange={(e) => void choose(e)}
            />
            <Button variant={check || done ? 'default' : 'primary'} busy={busy === 'checking'} onClick={() => picker.current?.click()}>
              {file ? t('import.chooseAnother') : t('import.choose')}
            </Button>
            {file ? <span className="small muted import-file">{file.name}</span> : null}
          </li>
          <li>
            <span>{t('import.step3')}</span>
          </li>
        </ol>
        {problem ? (
          <p role="alert" className="import-problem">{problem}</p>
        ) : null}
      </div>

      {done ? (
        <div className="card import-done">
          <div className="import-done-mark" aria-hidden>✓</div>
          <div style={{ flex: 1 }}>
            <div className="strong">{t('import.doneTitle')}</div>
            <div className="small muted">
              {t('import.doneLine', { products: done.products, addedTo: done.addedTo, pieces: done.pieces, qty: done.qty })}
            </div>
          </div>
          <Button variant="primary" onClick={() => navigate('/products')}>{t('import.seeProducts')}</Button>
        </div>
      ) : null}

      {check ? (
        <>
          <div className="grid c4">
            <div className="kpi accent">
              <div className="k">{t('import.newProducts')}</div>
              <div className="v tnum">{check.totals.newProducts}</div>
              {check.totals.addedTo ? <div className="d">{t('import.addedTo', { n: check.totals.addedTo })}</div> : null}
            </div>
            <div className="kpi">
              <div className="k">{t('import.pieces')}</div>
              <div className="v tnum">{check.totals.newPieces}</div>
              <div className="d">{t('import.piecesHint')}</div>
            </div>
            <div className="kpi">
              <div className="k">{t('import.stock')}</div>
              <div className="v tnum">{check.totals.qty}</div>
            </div>
            <div className="kpi">
              <div className="k">{t('import.already')}</div>
              <div className="v tnum">{check.totals.skipped}</div>
              <div className="d">{t('import.alreadyHint')}</div>
            </div>
          </div>

          {check.errors.length ? (
            <div className="card import-errors">
              <div className="strong">{t('import.errorsTitle', { n: check.errors.length + check.moreErrors })}</div>
              <p className="small muted" style={{ margin: 'var(--s-1) 0 var(--s-3)' }}>{t('import.errorsHint')}</p>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: 90 }}>{t('import.row')}</th>
                      <th>{t('import.problem')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {check.errors.map((e, i) => (
                      <tr key={i}>
                        <td className="tnum">{e.row > 0 ? e.row : '-'}</td>
                        <td>{say(`import.err.${e.code}`, { value: e.value ?? '' })}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {check.moreErrors ? <p className="small muted">{t('import.more', { n: check.moreErrors })}</p> : null}
            </div>
          ) : null}

          {check.warnings.length ? (
            <div className="card import-warnings">
              {check.warnings.map((w) => (
                <div key={w.code} className="small">{say(`import.warn.${w.code}`, { n: w.n ?? 0 })}</div>
              ))}
            </div>
          ) : null}

          <div className="card">
            <div className="small">
              <span className="muted">{t('import.readFrom', { sheet: check.sheet, row: check.headerRow })}</span>{' '}
              {Object.values(check.columns).map((h) => (
                <span key={h} className="import-chip">{h}</span>
              ))}
            </div>
            {check.ignored.length ? (
              <div className="small muted" style={{ marginTop: 'var(--s-2)' }}>
                {t('import.ignored')} {check.ignored.join(' · ')}
              </div>
            ) : null}
            {(['categories', 'colours', 'brands', 'suppliers'] as const).some((k) => check.create[k].length) ? (
              <div className="small" style={{ marginTop: 'var(--s-3)' }}>
                <div className="muted">{t('import.willCreate')}</div>
                {(['categories', 'colours', 'brands', 'suppliers'] as const).map((k) =>
                  check.create[k].length ? (
                    <div key={k} style={{ marginTop: 'var(--s-1)' }}>
                      <span className="strong">{t(`import.create.${k}` as StringKey)}:</span>{' '}
                      {check.create[k].map((x) => (
                        <span key={x} className="import-chip">{x}</span>
                      ))}
                    </div>
                  ) : null,
                )}
              </div>
            ) : null}
          </div>

          {check.products.length ? (
            <div className="card flush">
              <div className="table-wrap">
                <table className="cards">
                  <thead>
                    <tr>
                      <th>{t('common.product')}</th>
                      <th>{t('common.category')}</th>
                      <th>{t('import.sizesColours')}</th>
                      <th className="num">{t('common.price')}</th>
                      <th className="num">{t('import.pieces')}</th>
                      <th className="num">{t('import.stock')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {check.products.map((p) => (
                      <tr key={p.row} className={p.pieces === 0 ? 'import-skip' : ''}>
                        <td className="full strong" data-label={t('common.product')}>
                          {name(p)}
                          {p.existing ? <> <Pill kind="info">{p.pieces ? t('import.addsTo') : t('import.inSystem')}</Pill></> : null}
                          <div className="xsmall muted">{p.code || t('import.rowN', { n: p.row })}</div>
                        </td>
                        <td data-label={t('common.category')}>{p.category}</td>
                        <td data-label={t('import.sizesColours')} className="small">
                          {p.sizes.join(', ')}
                          {p.colours.length ? <div className="muted">{p.colours.join(', ')}</div> : null}
                        </td>
                        <td className="num tnum" data-label={t('common.price')}>{money(p.price)}</td>
                        <td className="num" data-label={t('import.pieces')}>{p.pieces}</td>
                        <td className="num" data-label={t('import.stock')}>{p.qty}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {check.moreProducts ? <p className="small muted" style={{ padding: 'var(--s-3) var(--s-4)' }}>{t('import.more', { n: check.moreProducts })}</p> : null}
            </div>
          ) : null}

          <div className="row wrap import-go">
            <div className="small muted" style={{ flex: '1 1 260px' }}>
              {check.errors.length ? t('import.fixFirst') : nothingNew ? t('import.nothingNew') : t('import.readyHint')}
            </div>
            <Button onClick={() => picker.current?.click()}>{t('import.chooseAnother')}</Button>
            <Button
              variant="primary"
              size="lg"
              busy={busy === 'importing'}
              disabled={check.errors.length > 0 || nothingNew}
              onClick={() => void doImport()}
            >
              {t('import.go', { n: check.totals.newPieces })}
            </Button>
          </div>
        </>
      ) : null}
    </div>
  );
}
